<template>
  <div class="line-items">
    <!-- Kit Details Section -->
    <div v-if="kitDetails" class="kit-details">
      <h2>Kit Details</h2>
      <div class="kit-info">
        <div class="info-row">
          <span class="label">Kit Number:</span>
          <span class="value">{{ kitDetails.KitNumber || '' }}</span>
        </div>
        <div class="info-row">
          <span class="label">Description:</span>
          <span class="value">{{ kitDetails.Description || '' }}</span>
        </div>
        <div class="info-row">
          <span class="label">Created By:</span>
          <span class="value">{{ kitDetails.UserName || '' }}</span>
        </div>
        <div class="info-row">
          <span class="label">Create Date:</span>
          <span class="value">{{ kitDetails.CreateDate ? formatDate(kitDetails.CreateDate) : '' }}</span>
        </div>
      </div>
    </div>

    <div class="header">
      <h1>Line Items</h1>
    </div>
    
    <div v-if="isLoading" class="loading">Loading line items...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else-if="lineItems && lineItems.length > 0" class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Part Number</th>
            <th>Quantity</th>
            <th>Hose</th>
            <th>Length</th>
            <th>Fitting A</th>
            <th>Fitting B</th>
            <th>Orientation</th>
            <th>VPC Length</th>
            <th>VPC Part</th>
            <th>Price</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in filteredItems" :key="item.LineID || index">
            <td>{{ item.CustPart || '' }}</td>
            <td>{{ item.Quantity || '' }}</td>
            <td>{{ item.Hose || '' }}</td>
            <td>{{ item.Length || '' }}</td>
            <td>{{ item.FittingA || '' }}</td>
            <td>{{ item.FittingB || '' }}</td>
            <td>{{ item.Orientation || '' }}</td>
            <td>{{ item.VPC ? (item.VPCLen || '') : '' }}</td>
            <td>{{ item.VPC ? (item.VPCPart || '') : '' }}</td>
            <td>
              <span v-if="getIqPrice(item.CustPart) !== null">€{{ formatPrice(getIqPrice(item.CustPart)) }}</span>
              <button 
                v-else
                @click="calculateItemCost(item)"
                class="btn-icon"
                title="Calculate Price"
              >
                €
              </button>
            </td>
            <td>
              <div class="action-buttons">
                <button 
                  class="details-btn"
                  @click="viewItemDetails(item)"
                >
                  Details
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="no-data">
      No line items found.
    </div>

    <!-- Item Details Modal -->
    <div v-if="selectedItem" class="modal-overlay" @click="closeItemDetails">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Item Details</h3>
          <button class="close-button" @click="closeItemDetails">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Fitting A Details -->
          <div class="form-section">
            <h4 class="form-section-title">Fitting A Details</h4>
            <div class="form-row">
              <div class="form-group">
                <label>Main Fitting (Coupling)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingA || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Fitting A1 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingA1 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Fitting A2 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingA2 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Fitting A3 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingA3 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Fitting A4 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingA4 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Fitting A5 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingA5 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
            </div>
          </div>

          <!-- Fitting B Details -->
          <div class="form-section">
            <h4 class="form-section-title">Fitting B Details</h4>
            <div class="form-row">
              <div class="form-group">
                <label>Fitting B (Coupling)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingB || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Fitting B1 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingB1 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Fitting B2 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingB2 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Fitting B3 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingB3 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Fitting B4 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingB4 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Fitting B5 (Adaptor)</label>
                <input 
                  type="text" 
                  :value="selectedItem.FittingB5 || ''" 
                  class="form-input"
                  readonly
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Orientation</label>
                <input 
                  type="text" 
                  :value="selectedItem.Orientation || ''" 
                  class="form-input"
                  readonly
                />
              </div>
            </div>
          </div>

          <!-- Additional Details -->
          <div class="form-section">
            <h4 class="form-section-title">Additional Details</h4>
            <div class="form-row">
              <div class="form-group">
                <label>Hose</label>
                <input 
                  type="text" 
                  :value="selectedItem.Hose || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Overall Length</label>
                <input 
                  type="text" 
                  :value="selectedItem.Length || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>Cut Length</label>
                <input 
                  type="text" 
                  :value="selectedItem.CutLength || ''" 
                  class="form-input"
                  readonly
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group checkbox-group">
                <label class="checkbox-label">
                  <input 
                    type="checkbox" 
                    :checked="selectedItem.VPC || false"
                    disabled
                  /> VPC
                </label>
              </div>
            </div>
            <div v-if="selectedItem.VPC" class="form-row">
              <div class="form-group">
                <label>VPC Part</label>
                <input 
                  type="text" 
                  :value="selectedItem.VPCPart || ''" 
                  class="form-input"
                  readonly
                />
              </div>
              <div class="form-group">
                <label>VPC Length</label>
                <input 
                  type="text" 
                  :value="selectedItem.VPCLen || ''" 
                  class="form-input"
                  readonly
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Note</label>
                <textarea 
                  :value="selectedItem.Note || ''" 
                  class="form-input"
                  rows="3"
                  readonly
                ></textarea>
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button 
              type="button" 
              @click="closeItemDetails" 
              class="cancel-button"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Cost Calculation Modal -->
    <div v-if="showCostModal" class="modal-overlay">
      <div class="modal-content cost-modal">
        <div class="modal-header">
          <h3 v-if="isAdminOrManager">Cost Breakdown - {{ costModalPart || '-' }}</h3>
          <h3 v-else>{{ costModalPart || '-' }}</h3>
          <div class="modal-header-right">
            <span v-if="!isAdminOrManager">Qty: {{ costModalQty || 1 }}</span>
            <button @click="closeCostModal" class="close-button">&times;</button>
          </div>
        </div>
        <div class="modal-body">
          <div v-if="selectedItemCost" class="cost-breakdown">
            <div class="cost-items" v-if="isAdminOrManager">
              <div class="cost-item">
                <span class="label">Hose Cost</span>
                <span class="value">€{{ Number(selectedItemCost.breakdown.hoseCost).toFixed(2) }}</span>
              </div>
              <div class="cost-item">
                <span class="label">VPC Cost</span>
                <span class="value">€{{ Number(selectedItemCost.breakdown.vpcCost).toFixed(2) }}</span>
              </div>
              <div class="cost-item">
                <span class="label">Fittings Cost</span>
                <span class="value">€{{ Number(selectedItemCost.breakdown.fittingsCost).toFixed(2) }}</span>
              </div>
              <div class="cost-item">
                <span class="label">Labels Cost</span>
                <span class="value">€{{ Number(selectedItemCost.breakdown.labelsCost).toFixed(2) }}</span>
              </div>
            </div>
            <div class="cost-total">
              <div class="total-divider"></div>
              <div class="total-row">
                <span class="total-label">Total Price</span>
                <span class="total-value">€{{ Number(selectedItemCost.totalCost).toFixed(2) }}</span>
              </div>
            </div>
          </div>
          <div v-else class="loading-cost">
            <div class="loading-spinner"></div>
            <span>Calculating costs...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LineItemsView',
  data() {
    return {
      lineItems: [],
      searchQuery: '',
      isLoading: true,
      error: null,
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 10,
      searchTimeout: null,
      userRole: '',
      kitId: null,
      kitDetails: null,
      itemsCustomer: null,
      selectedItem: null,
      showCostModal: false,
      selectedItemCost: null,
      costModalPart: null,
      costModalQty: 1,
      // IQ price cache by CustPart (unit price)
      iqPrices: {},
      // IQ existence cache by CustPart
      iqExists: {}
    }
  },
  computed: {
    filteredItems() {
      if (!Array.isArray(this.lineItems)) {
        return [];
      }
      
      let items = this.lineItems;
      
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        items = items.filter(item => {
          if (!item) return false;
          return (item.CustPart && item.CustPart.toLowerCase().includes(query)) ||
                 (item.Hose && item.Hose.toLowerCase().includes(query));
        });
      }
      
      return items;
    },
    hasVPCItems() {
      return this.filteredItems.some(item => item && item.VPC === true);
    },
    isAdminOrManager() {
      return this.userRole === 'Admin' || this.userRole === 'Manager';
    }
  },
  methods: {
    async checkAuth() {
      try {
        const response = await axios.get('/auth/check-auth');
        this.userRole = response.data.user.role;
      } catch (error) {
        console.error('Error checking auth:', error);
        this.userRole = '';
      }
    },
    extractKitDetails() {
      if (Array.isArray(this.lineItems) && this.lineItems.length > 0) {
        const firstItem = this.lineItems[0];
        if (firstItem) {
          this.kitDetails = {
            KitNumber: firstItem.KitID || '',
            Description: firstItem.Description || '',
            Status: firstItem.Status || 'Unknown',
            UserName: firstItem.UserName || 'Unknown',
            CreateDate: firstItem.CreateDate || firstItem.LastModDate || new Date().toISOString()
          };
          this.itemsCustomer = firstItem.Customer || null;
        }
      } else {
        this.kitDetails = null;
        this.itemsCustomer = null;
      }
    },
    async fetchLineItems() {
      this.isLoading = true;
      this.error = null;
      
      try {
        let response;
        
        if (this.kitId) {
          response = await axios.get('/lineitems/searchkit', {
            params: {
              kitId: this.kitId
            }
          });
        } else {
          response = await axios.get('/lineitems', {
            params: {
              searchTerm: this.searchQuery,
              page: this.currentPage,
              limit: this.itemsPerPage
            }
          });
        }
        
        this.lineItems = Array.isArray(response.data) ? response.data : [];
        this.totalItems = this.lineItems.length;
        this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
        
        if (this.kitId) {
          this.extractKitDetails();
        }

        // Load IQ prices asynchronously after initial render
        this.$nextTick(() => {
          this.loadIqPricesForItems(this.lineItems);
        });
      } catch (error) {
        console.error('Error fetching line items:', error);
        this.error = error.response?.data?.message || 'Failed to fetch line items';
        this.lineItems = [];
      } finally {
        this.isLoading = false;
      }
    },
    async loadIqPricesForItems(items) {
      try {
        const productCodesRaw = (items || []).map(i => i.CustPart).filter(Boolean);
        if (productCodesRaw.length === 0) return;

        // Deduplicate to reduce calls
        const productCodes = Array.from(new Set(productCodesRaw));

        // Batch in chunks to satisfy API limit (<= 100 per request)
        const chunkSize = 100;
        const chunks = [];
        for (let i = 0; i < productCodes.length; i += chunkSize) {
          chunks.push(productCodes.slice(i, i + chunkSize));
        }

        console.log(`Pricing lookup: ${productCodes.length} unique codes in ${chunks.length} batch(es)`);

        const priceMap = { ...this.iqPrices };
        const existsMap = { ...this.iqExists };

        for (let idx = 0; idx < chunks.length; idx++) {
          const codes = chunks[idx];
          const body = { productCodes: codes };
          if (this.isAdminOrManager && this.itemsCustomer) {
            body.customer = this.itemsCustomer;
          }

          console.log(`Pricing batch ${idx + 1}/${chunks.length}: ${codes.length} codes`);
          // eslint-disable-next-line no-await-in-loop
          const resp = await axios.post('/product-validation/batch', body);
          const results = resp.data?.results || [];

          for (const r of results) {
            if (r && r.success && r.exists) {
              let data = r.data;
              if (data && Array.isArray(data)) data = data[0];
              if (data && data.Data && Array.isArray(data.Data)) data = data.Data[0];
              const price = Number(data?.SellingPrice1 ?? data?.Selling?.SellingPrice1);
              const key = r.productCode || data?.Code || null;
              if (key) {
                existsMap[key] = true;
                if (!isNaN(price) && price > 0) {
                  priceMap[key] = price;
                }
              }
            }
          }
          // Update progressively so UI reflects loaded batches
          this.iqPrices = { ...this.iqPrices, ...priceMap };
          this.iqExists = { ...this.iqExists, ...existsMap };
        }
      } catch (e) {
        console.warn('Failed to load IQ prices (LineItemsView):', e.message);
      }
    },
    getIqPrice(custPart) {
      if (!custPart) return null;
      return this.iqPrices[custPart] ?? null;
    },
    getIqExists(custPart) {
      if (!custPart) return false;
      return !!this.iqExists[custPart];
    },
    formatPrice(value) {
      const n = Number(value);
      if (isNaN(n)) return '-';
      return n.toFixed(2);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return '';
      }
    },
    getStatusClass(status) {
      if (!status) return 'status-default';
      
      switch (status.toLowerCase()) {
        case 'pending':
          return 'status-pending';
        case 'in progress':
          return 'status-progress';
        case 'completed':
          return 'status-completed';
        case 'cancelled':
          return 'status-cancelled';
        default:
          return 'status-default';
      }
    },
    viewItemDetails(item) {
      if (item && typeof item === 'object') {
        this.selectedItem = item;
      }
    },
    closeItemDetails() {
      this.selectedItem = null;
    },
    async calculateItemCost(item) {
      this.showCostModal = true;
      this.selectedItemCost = null;
      this.costModalPart = item?.CustPart || item?.BillPart || null;
      this.costModalQty = item?.Quantity || 1;

      try {
        console.log('Calculating cost for kit line item:', {
          LineID: item.LineID,
          CustPart: item.CustPart,
          Hose: item.Hose,
          CutLength: item.CutLength,
          FittingA: item.FittingA,
          FittingB: item.FittingB
        });

        // Use GET endpoint with LineID - backend will lookup kit label data automatically
        const response = await axios.get(`/cost-calculation/calculate-item/${item.LineID}`);

        if (response.data && response.data.success) {
          this.selectedItemCost = response.data.data;
        } else {
          console.error('Invalid cost calculation response:', response.data);
          this.error = 'Failed to calculate cost';
        }
      } catch (error) {
        console.error('Error calculating cost for kit item:', error);
        this.error = 'Failed to calculate cost: ' + (error.response?.data?.message || error.message);
      }
    },
    closeCostModal() {
      this.showCostModal = false;
      this.selectedItemCost = null;
    }
  },
  watch: {
    '$route.params': {
      immediate: true,
      handler(params) {
        this.kitId = params.kitId ? parseInt(params.kitId) : null;
        this.fetchLineItems();
      }
    }
  },
  mounted() {
    this.checkAuth();
    this.fetchLineItems();
  }
}
</script>

<style scoped>
.line-items {
  padding: 2rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

h1, h2 {
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

h2 {
  margin-bottom: 1rem;
}

.kit-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
}

.kit-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.label {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.value {
  font-size: 1rem;
  color: var(--text-primary);
  font-weight: 500;
}

.table-container {
  background: var(--background);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.data-table th {
  background-color: #f8f8f8;
  font-weight: 600;
  color: var(--text-primary);
}

.data-table tr:hover {
  background-color: rgba(220, 38, 38, 0.02);
}

.loading, .error, .no-data {
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
}

.error {
  background-color: #fee2e2;
  color: #991b1b;
  border-radius: 0.5rem;
  border: 1px solid #fecaca;
}

.details-btn {
  padding: 0.5rem 1rem;
  border: 1px solid var(--primary-color);
  border-radius: 4px;
  background-color: var(--primary-color);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.details-btn:hover {
  background-color: #c53030;
  border-color: #c53030;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  line-height: 1;
}

.close-button:hover {
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
}

/* Add spacing between Qty label and close button in modal header */
.modal-header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem; /* adjust spacing as needed */
}

.form-section {
  margin-bottom: 2rem;
}

.form-section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
}

.form-input {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: #f9fafb;
}

.form-input:read-only {
  cursor: not-allowed;
}

.checkbox-group {
  display: flex;
  align-items: center;
  margin-top: 1rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.checkbox-label input[type="checkbox"] {
  margin-right: 0.5rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.cancel-button {
  padding: 0.75rem 1.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background-color: white;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

@media (max-width: 768px) {
  .line-items {
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .data-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
}

/* Action buttons container */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

/* Money icon button styles */
.btn-icon {
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  min-width: 32px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background-color: #218838;
}

.btn-icon:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* Cost modal styles - reused from LineItemSearchView */
.cost-modal {
  max-width: 500px;
  padding: 0;
  overflow: hidden;
}

.cost-modal .modal-header {
  background-color: var(--primary-color);
  color: white;
  padding: 1.25rem 1.5rem;
  margin: 0;
}

.cost-modal .modal-header h3 {
  color: white;
  margin: 0;
  font-size: 1.5rem;
}

.cost-modal .close-button {
  color: white;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.cost-modal .close-button:hover {
  opacity: 1;
}

.cost-modal .modal-body {
  padding: 1.5rem;
}

.cost-breakdown {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cost-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cost-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: var(--background-alt);
  border-radius: 0.5rem;
  transition: transform 0.2s ease;
}

.cost-item:hover {
  transform: translateX(4px);
}

.cost-item .label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 1rem;
}

.cost-item .value {
  font-weight: 600;
  color: var(--primary-color);
  font-size: 1.1rem;
}

.cost-total {
  margin-top: 1rem;
}

.total-divider {
  height: 1px;
  background-color: #e5e7eb;
  margin: 1rem 0;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: var(--primary-color);
  border-radius: 0.5rem;
  color: white;
}

.total-label {
  font-size: 1.25rem;
  font-weight: 600;
}

.total-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.loading-cost {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--background-alt);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 